import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';

import { Button } from '@/shadcn/button';
import { DialogClose, DialogDescription, DialogFooter } from '@/shadcn/dialog';

import { ROUTE_WORKFLOW_DASHBOARD } from '@/constant/routes';
import { USER_ORGANIZATION_TEAMS_QUERY } from '@/gql/queries/groups/addTeams.query.gql';
import { useErrorContext } from '@/hooks/useErrorContext';
import { TestId } from '@/types/testId';
import { pluralize } from '@/utils';
import { TeamItem } from './teamItem/teamItem';

type Props = {
  groupName: string;
  submit: (teamsIds: number[]) => void;
};

// Modal content to add a set of teams that belong to the organization
export const AddTeams = memo(({ groupName, submit }: Props) => {
  const { data, loading, error } = useQuery(USER_ORGANIZATION_TEAMS_QUERY, {
    fetchPolicy: 'cache-and-network'
  });

  const me = data?.me;

  const { handleError } = useErrorContext();

  // Raise 404 page when group not found.
  const groupError = error || !(loading || me);
  useEffect(() => {
    // This check avoids rerendering the component after the errorBoundary has been triggered
    if (groupError) {
      handleError('user', undefined, ROUTE_WORKFLOW_DASHBOARD, error);
    }
  }, [groupError, handleError]);

  // state for handling list of teams accountIds
  const [teamsIds, setTeamsIds] = useState<number[]>([]);

  // callback to toggle a team being selected for addition to the group
  const toggleTeam = useCallback(
    (teamId: number) => {
      const newTeamsIds = [...teamsIds];
      const teamIdx = newTeamsIds.indexOf(teamId);
      if (teamIdx > -1) {
        newTeamsIds.splice(teamIdx, 1);
      } else {
        newTeamsIds.push(teamId);
      }
      setTeamsIds(newTeamsIds);
    },
    [teamsIds]
  );

  // memoized parsed organization teams
  const teamsList = useMemo(() => {
    if (loading) return null;

    const organization = me?.organizations.nodes[0];
    if (!organization) return null;

    const organizationTeams = organization.teams?.nodes || [];

    return organizationTeams
      .filter(team => !!team)
      .map(team => ({
        accountId: team.accountId,
        name: team.name,
        avatar: team.avatar,
        nbParticipants: team.members.totalCount + team.bots.totalCount
      }))
      .sort((team1, team2) => team1.name.localeCompare(team2.name));
  }, [loading, me]);

  // memoized teams components
  const teamsComponents = useMemo(() => {
    if (loading)
      return (
        <div className="flex flex-col flex-nowrap items-stretch gap-1 p-1">
          {Array.from({ length: 7 }).map((_, index) => (
            <TeamItem key={index} loading />
          ))}
        </div>
      );

    if (!teamsList)
      return (
        <div className="pt-2.5 text-base">
          You don&#39;t belong to any organization
        </div>
      );

    if (!teamsList.length)
      return (
        <div className="pt-2.5 text-base">
          No teams added to this organization yet
        </div>
      );

    return (
      <div className="flex flex-col flex-nowrap items-stretch gap-1 p-1">
        {teamsList.map(({ accountId, avatar, name, nbParticipants }) => (
          <TeamItem
            accountId={accountId}
            avatar={avatar}
            isSelected={teamsIds.includes(accountId)}
            key={accountId}
            name={name}
            nbParticipants={nbParticipants}
            toggleTeam={toggleTeam}
          />
        ))}
      </div>
    );
  }, [loading, teamsList, teamsIds, toggleTeam]);

  return (
    <>
      <section>
        <DialogDescription className="py-2 text-justify text-muted-foreground text-xs">
          Select teams from the list below to add them to the{' '}
          <span className="font-semibold text-primary">{groupName}</span> group.{' '}
          Note that all the participants of the selected team will become
          participants of the workflow.
        </DialogDescription>
        <div className="scrollbar-thin scrollbar-stable max-h-[320px] min-h-[54px] overflow-auto">
          {teamsComponents}
        </div>
      </section>
      <DialogFooter className="flex w-full justify-between pt-6">
        <DialogClose asChild>
          <Button data-cy={TestId.AddTeams.CancelButton} variant="outline">
            cancel
          </Button>
        </DialogClose>
        <Button
          data-cy={TestId.AddTeams.SubmitButton}
          disabled={!teamsIds.length}
          onClick={() => submit(teamsIds)}
        >
          {teamsIds.length
            ? `Add ${pluralize(teamsIds.length, 'team', false, true)}`
            : 'Add team'}
        </Button>
      </DialogFooter>
    </>
  );
});
